/**
 * Onboarding router — the post-login entry point (application-form-builder spec,
 * task 21.2).
 *
 * After an Applicant signs in, this component is the single gate that decides
 * where they go next. It asks the transport seam for the
 * `getOnboardingDestination` decision — which runs the pure
 * `onboardingRouting.ts#resolveDestination` rule server-side over the
 * Applicant's derived Person + Participation state (Req 15.5) — and routes per
 * the resolved `Destination` (Req 15.1–15.4). The destination is never a fixed
 * constant: it is recomputed every time this component loads (and again right
 * after contact intake completes), so the Applicant always lands on the correct
 * next step.
 *
 * ## The blocking intake gate (Req 14.8, 15.1)
 *
 * `resolveDestination` returns `CONTACT_INTAKE` first, before anything else, for
 * any Applicant who has not completed the fixed Contact_Intake_Form. This
 * component honors that gate by rendering the `ContactIntakeForm` **inline** for
 * that destination rather than emitting a navigation — so an Applicant who has
 * not completed intake can never reach the application, the resume view, the
 * status view, or the dashboard. Only once intake completes (the form's
 * `onComplete` fires and we re-resolve the destination) can any other
 * destination be produced. Entry to the application is therefore structurally
 * blocked until intake is done.
 *
 * ## Decoupled navigation for resolved destinations
 *
 * The screens the other destinations open — the application renderer (task 23),
 * the application status view, and the dashboard — live outside this onboarding
 * surface. So for every non-intake destination the router calls the
 * parent-supplied `onResolve` callback with the resolved `Destination`, letting
 * the host app perform the actual navigation. This keeps the router self-
 * contained: it owns the *decision* and the intake gate, the host owns the
 * *navigation*.
 *
 * ## Accessibility (WCAG AA)
 *
 * The loading state is an `aria-live` `role="status"` region so the transition
 * is announced; the error state is a `role="alert"` region with a keyboard-
 * reachable retry control.
 *
 * Validates: Requirements 14.8, 15.1, 15.2, 15.3, 15.4, 15.5
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';

import { Button } from '@/components/ui/button';
import { applicationFormClient } from '@/lib/application-form/client';
import type { Destination } from '@/lib/application-form/client';

import { ContactIntakeForm } from './ContactIntakeForm';
import type { ContactIntakeFormProps } from './ContactIntakeForm';

/**
 * A resolved onboarding destination that is *not* the inline contact-intake
 * gate — i.e. one the host app navigates to (Req 15.2, 15.3, 15.4, and the
 * default DASHBOARD). The `CONTACT_INTAKE` case is handled inline by this
 * component and is therefore never emitted to `onResolve`.
 */
export type ResolvedDestination = Exclude<Destination, { kind: 'CONTACT_INTAKE' }>;

/**
 * The maximum time (`Resolution_Timeout`, Req 1.6) the router waits for
 * `getOnboardingDestination()` before treating resolution as failed and
 * surfacing the error state.
 */
const RESOLUTION_TIMEOUT_MS = 10_000;

/**
 * The complete set of defined onboarding destination kinds (Req 1.7). Any
 * resolved value whose `kind` is outside this set — including a non-object or a
 * value missing `kind` entirely — is treated as a failed resolution rather than
 * rendered as an undefined surface.
 */
const KNOWN_DESTINATION_KINDS = new Set<Destination['kind']>([
  'CONTACT_INTAKE',
  'APPLICATION',
  'RESUME_RESPONSE',
  'APPLICATION_STATUS',
  'DASHBOARD',
]);

/**
 * Narrows an arbitrary resolved value to a defined `Destination` (Req 1.7).
 * Guards against non-object values, a missing/non-string `kind`, and any `kind`
 * outside the known set, so an out-of-set resolution can be funneled into the
 * error state instead of rendering an undefined surface.
 */
function isKnownDestination(value: unknown): value is Destination {
  if (typeof value !== 'object' || value === null) return false;
  const kind = (value as { kind?: unknown }).kind;
  return typeof kind === 'string' && KNOWN_DESTINATION_KINDS.has(kind as Destination['kind']);
}

export interface OnboardingRouterProps {
  /**
   * Called once onboarding resolves to a destination other than
   * `CONTACT_INTAKE`. The host performs the actual navigation (the application
   * renderer, the status view, and the dashboard live outside the onboarding
   * surface). Receives the full resolved `Destination` so the application-bound
   * kinds carry the `competitionId` they resolved against (Req 15.2–15.4).
   */
  onResolve: (destination: ResolvedDestination) => void;
  /**
   * Called when the router resolves the blocking `CONTACT_INTAKE` gate — i.e.
   * the member had NOT completed contact intake and the inline
   * `ContactIntakeForm` is being shown. Lets the host distinguish a member who
   * *just completed intake this session* (intake was shown, then re-resolved to
   * a non-intake destination) from one who was *already onboarded* (intake was
   * never shown). The host uses this to forward to the dashboard only after an
   * actual intake completion (member-onboarding-flow Req 1.3) and otherwise
   * render, in place, the surface the member intentionally navigated to.
   */
  onIntakePending?: () => void;
  /**
   * Called when the router enters its terminal error state (resolution rejected,
   * timed out, returned an unknown destination, or looped post-intake). Lets the
   * host FAIL OPEN — proceed into the portal instead of trapping the member on
   * the retry screen — when the onboarding backend is unavailable (e.g. the live
   * resolver failing on the deployed dev build). When provided, the host may
   * unmount this router in favor of the protected surface, so the built-in
   * alert+retry UI is a fallback for hosts that do NOT pass this.
   */
  onUnresolvable?: (error: Error) => void;
  /**
   * Optional starting values for the inline Contact_Intake screen (e.g. a
   * prefilled email from the account).
   */
  intakeInitialValues?: ContactIntakeFormProps['initialValues'];
}

type LoadState =
  | { status: 'loading' }
  | { status: 'error'; error: Error }
  | { status: 'resolved'; destination: Destination };

/**
 * The post-login onboarding router. Resolves the onboarding destination from
 * the transport seam, renders the blocking Contact_Intake gate inline for
 * `CONTACT_INTAKE`, and hands every other resolved destination to `onResolve`.
 */
export function OnboardingRouter({
  onResolve,
  onIntakePending,
  onUnresolvable,
  intakeInitialValues,
}: OnboardingRouterProps): React.ReactElement {
  const [state, setState] = useState<LoadState>({ status: 'loading' });

  // Keep the latest onResolve without retriggering the resolve effect when the
  // host passes a fresh closure each render.
  const onResolveRef = useRef(onResolve);
  useEffect(() => {
    onResolveRef.current = onResolve;
  }, [onResolve]);

  // Same latest-closure handling for the optional intake-pending signal.
  const onIntakePendingRef = useRef(onIntakePending);
  useEffect(() => {
    onIntakePendingRef.current = onIntakePending;
  }, [onIntakePending]);

  // Same latest-closure handling for the optional fail-open signal.
  const onUnresolvableRef = useRef(onUnresolvable);
  useEffect(() => {
    onUnresolvableRef.current = onUnresolvable;
  }, [onUnresolvable]);

  // Tracks whether the component is still mounted so an in-flight resolve never
  // sets state after unmount.
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // Post-intake loop guard (Req 2.6, 5.4): set to true only for the single
  // re-resolution kicked off by a successful contact-intake submission. A
  // completed Contestant must never be sent back to the intake gate, so if that
  // specific re-resolution still returns CONTACT_INTAKE we treat it as a failed
  // resolution (error + retry) instead of re-rendering the form. The flag is
  // captured-and-cleared at the start of every resolve, so a manual retry from
  // the error state is always a fresh resolution (CONTACT_INTAKE then renders
  // the form normally, e.g. on the initial gate).
  const intakeJustCompletedRef = useRef(false);

  const resolve = useCallback(async () => {
    // Capture-and-clear the post-intake flag for this run only. Any subsequent
    // resolve (including the error-state "Try again" retry) starts fresh.
    const wasPostIntake = intakeJustCompletedRef.current;
    intakeJustCompletedRef.current = false;
    setState({ status: 'loading' });
    let timeoutId: ReturnType<typeof setTimeout> | undefined;
    try {
      // Race the resolution against the 10s Resolution_Timeout (Req 1.6). When
      // the timeout wins, Promise.race settles on the rejection and the
      // late-arriving getOnboardingDestination() result is discarded — it can
      // never reach setState, so it cannot overwrite the error state. The
      // mountedRef guard below still blocks any state write after unmount.
      const destination = await Promise.race<Destination>([
        applicationFormClient.getOnboardingDestination(),
        new Promise<never>((_, reject) => {
          timeoutId = setTimeout(
            () => reject(new Error('Onboarding destination resolution timed out')),
            RESOLUTION_TIMEOUT_MS,
          );
        }),
      ]);
      // Unknown-destination guard (Req 1.7): a resolved value whose kind is
      // outside the defined Destination set (or a non-object / missing kind) is
      // a failed resolution, not a renderable surface — route it to the error
      // state via the same path as a rejection/timeout rather than ever
      // rendering an undefined surface.
      if (!isKnownDestination(destination)) {
        throw new Error(`Unknown onboarding destination: ${JSON.stringify(destination)}`);
      }
      // Post-intake loop guard (Req 2.6, 5.4): the re-resolution that follows a
      // successful submitContactIntake must advance past the gate. If it still
      // returns CONTACT_INTAKE, withhold re-rendering ContactIntakeForm and
      // funnel into the error state (alert + retry) via the same catch path —
      // so a completed Contestant can never be trapped in an intake loop.
      if (wasPostIntake && destination.kind === 'CONTACT_INTAKE') {
        throw new Error('Onboarding re-resolved to contact intake after completion');
      }
      if (mountedRef.current) setState({ status: 'resolved', destination });
    } catch (err) {
      if (mountedRef.current) {
        setState({
          status: 'error',
          error: err instanceof Error ? err : new Error(String(err)),
        });
      }
    } finally {
      if (timeoutId !== undefined) clearTimeout(timeoutId);
    }
  }, []);

  // Resolve the destination on mount.
  useEffect(() => {
    void resolve();
  }, [resolve]);

  // Hand every resolved non-intake destination to the host for navigation, and
  // signal the host when the blocking CONTACT_INTAKE gate is shown. Done in an
  // effect (never during render) so navigation side effects stay safe.
  useEffect(() => {
    if (state.status !== 'resolved') return;
    if (state.destination.kind === 'CONTACT_INTAKE') {
      onIntakePendingRef.current?.();
    } else {
      onResolveRef.current(state.destination);
    }
  }, [state]);

  // Fail-open signal: when resolution terminally fails, tell the host so it can
  // proceed into the protected surface instead of leaving the member stuck on
  // the retry screen (contestant-onboarding-intake resilience). Fired from an
  // effect (never during render) so the host's state update is safe. Hosts that
  // do not pass `onUnresolvable` keep the built-in alert+retry fallback below.
  useEffect(() => {
    if (state.status === 'error') {
      onUnresolvableRef.current?.(state.error);
    }
  }, [state]);

  // After contact intake completes, re-resolve: the Applicant is no longer at
  // the CONTACT_INTAKE gate, so the router advances to the real destination.
  // Mark this single re-resolution as post-intake so the loop guard (Req 2.6,
  // 5.4) can catch a stale CONTACT_INTAKE rather than re-rendering the form.
  const handleIntakeComplete = useCallback(() => {
    intakeJustCompletedRef.current = true;
    void resolve();
  }, [resolve]);

  if (state.status === 'loading') {
    return (
      <div
        className="flex min-h-[40vh] items-center justify-center px-5 py-16 text-center"
        role="status"
        aria-live="polite"
        data-testid="onboarding-router-loading"
      >
        <p className="text-muted-foreground font-body text-lg">Getting things ready…</p>
      </div>
    );
  }

  if (state.status === 'error') {
    return (
      <div
        className="mx-auto max-w-md space-y-4 px-5 py-16 text-center"
        data-testid="onboarding-router-error"
      >
        <div
          className="bg-destructive-muted text-destructive-text rounded-lg px-3 py-2 text-sm font-body"
          role="alert"
        >
          We couldn’t work out where to take you. Please try again.
        </div>
        <Button type="button" onClick={() => void resolve()} data-testid="onboarding-router-retry">
          Try again
        </Button>
      </div>
    );
  }

  // Resolved. The blocking intake gate renders inline; any other destination is
  // handed to the host (in the effect above) while we show a brief transition.
  if (state.destination.kind === 'CONTACT_INTAKE') {
    return (
      <ContactIntakeForm
        onComplete={handleIntakeComplete}
        initialValues={intakeInitialValues}
      />
    );
  }

  return (
    <div
      className="flex min-h-[40vh] items-center justify-center px-5 py-16 text-center"
      role="status"
      aria-live="polite"
      data-testid="onboarding-router-redirecting"
    >
      <p className="text-muted-foreground font-body text-lg">Taking you to the right place…</p>
    </div>
  );
}

export default OnboardingRouter;
